import React, { useState, useEffect } from "react";
import useItemsStore from "../../../store/itemsStore";
import useCategoriesStore from "../../../store/categoriesStore";
import useAdminAPI from "../../../hooks/useAdminAPI";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";

const Items = () => {
    const { items, updateItem } = useItemsStore();
    const { activeCategory } = useCategoriesStore();

    const [activeItems, setActiveItems] = useState([]);
    const { addItemAPI, deleteItemAPI, updateItemAPI } = useAdminAPI();

    const [showDialog, setShowDialog] = useState(false);
    const [showAddItemForm, setShowAddItemForm] = useState(false);

    const [editableItemId, setEditableItemId] = useState(null);

    const [newItem, setNewItem] = useState({
        title: "",
        description: "",
        prices: [{ description: "", amount: 0 }],
        sideDish: false,
    });

    const [itemToDelete, setItemToDelete] = useState(null);

    useEffect(() => {
        if (itemToDelete) {
            setShowDialog(true);
        }
    }, [itemToDelete]);

    useEffect(() => {
        if (activeCategory) {
            setActiveItems(
                items.filter((item) => item.category === activeCategory)
            );
        }
    }, [activeCategory, items]);

    const handleAddPrice = () => {
        setNewItem((prevItem) => ({
            ...prevItem,
            prices: [...prevItem.prices, { description: "", amount: 0 }],
        }));
    };

    const handleRemovePrice = (index) => {
        setNewItem((prevItem) => ({
            ...prevItem,
            prices: prevItem.prices.filter((_, i) => i !== index),
        }));
    };

    const handleSaveNewItem = async () => {
        if (!activeCategory) return;

        const itemToAdd = {
            ...newItem,
            category: activeCategory,
        };

        await addItemAPI(itemToAdd);
        setNewItem({
            title: "",
            description: "",
            prices: [{ description: "", amount: 0 }],
            sideDish: false,
        });
        setShowAddItemForm(false);
    };

    return (
        <div>
            {showDialog && (
                <ConfirmationDialog
                    title="Delete Item"
                    message="Are you sure you want to delete this item?"
                    onConfirm={() => {
                        setShowDialog(false);
                        deleteItemAPI(itemToDelete._id);
                        setItemToDelete(null);
                    }}
                    onCancel={() => {
                        setShowDialog(false);
                        setItemToDelete(null);
                    }}
                />
            )}
            <h1>Items</h1>

            <button
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 mb-4"
                onClick={() => setShowAddItemForm(!showAddItemForm)}
            >
                {showAddItemForm ? "Cancel" : "Add New Item"}
            </button>

            {showAddItemForm && (
                <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                    <h2 className="text-lg font-bold mb-4">Add New Item</h2>
                    <div className="grid grid-cols-1 gap-4">
                        <input
                            type="text"
                            placeholder="Title"
                            value={newItem.title}
                            onChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    title: e.target.value,
                                })
                            }
                            className="border rounded-md p-2"
                        />
                        <input
                            type="text"
                            placeholder="Description"
                            value={newItem.description}
                            onChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    description: e.target.value,
                                })
                            }
                            className="border rounded-md p-2"
                        />
                        <div>
                            <label className="font-medium">Prices:</label>
                            {newItem.prices.map((price, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2 mb-2"
                                >
                                    <input
                                        type="text"
                                        placeholder="Description"
                                        value={price.description}
                                        onChange={(e) => {
                                            const updatedPrices = [
                                                ...newItem.prices,
                                            ];
                                            updatedPrices[index].description =
                                                e.target.value;
                                            setNewItem({
                                                ...newItem,
                                                prices: updatedPrices,
                                            });
                                        }}
                                        className="border rounded-md p-2 flex-grow"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Amount"
                                        value={price.amount}
                                        onChange={(e) => {
                                            const updatedPrices = [
                                                ...newItem.prices,
                                            ];
                                            updatedPrices[index].amount =
                                                parseFloat(e.target.value);
                                            setNewItem({
                                                ...newItem,
                                                prices: updatedPrices,
                                            });
                                        }}
                                        className="border rounded-md p-2 w-24"
                                    />
                                    <button
                                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                        onClick={() => handleRemovePrice(index)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                                onClick={handleAddPrice}
                            >
                                Add Price
                            </button>
                        </div>
                        <input
                            type="number"
                            placeholder="Sort Order"
                            value={newItem.sortOrder}
                            onChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    sortOrder: parseInt(e.target.value),
                                })
                            }
                            className="border rounded-md p-2"
                        />
                        {
                            //Checkbox for boolean sideDish

                            <input
                                type="checkbox"
                                id="sideDish"
                                name="sideDish"
                                value="sideDish"
                                onChange={(e) =>
                                    setNewItem({
                                        ...newItem,
                                        sideDish: e.target.checked,
                                    })
                                }
                            />
                        }
                        <button
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700"
                            onClick={handleSaveNewItem}
                        >
                            Save Item
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2">
                {activeItems.map((item) => (
                    <div key={item._id} className="border rounded-md p-4 mb-2">
                        {editableItemId === item._id ? (
                            <>
                                {/* Editable Title */}
                                <input
                                    type="text"
                                    value={item.title}
                                    onChange={(e) =>
                                        setActiveItems((prevItems) =>
                                            prevItems.map((i) =>
                                                i._id === item._id
                                                    ? {
                                                          ...i,
                                                          title: e.target.value,
                                                      }
                                                    : i
                                            )
                                        )
                                    }
                                    className="border rounded-md p-2 w-full mb-2"
                                />

                                {/* Editable Description */}
                                <textarea
                                    value={item.description}
                                    onChange={(e) =>
                                        setActiveItems((prevItems) =>
                                            prevItems.map((i) =>
                                                i._id === item._id
                                                    ? {
                                                          ...i,
                                                          description:
                                                              e.target.value,
                                                      }
                                                    : i
                                            )
                                        )
                                    }
                                    className="border rounded-md p-2 w-full mb-2"
                                />

                                {/* Editable Prices */}
                                <div>
                                    <strong>Prices:</strong>
                                    <div className="pl-4">
                                        {item.prices.map((price, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 mb-2"
                                            >
                                                <input
                                                    type="text"
                                                    placeholder="Description"
                                                    value={price.description}
                                                    onChange={(e) => {
                                                        const updatedPrices = [
                                                            ...item.prices,
                                                        ];
                                                        updatedPrices[
                                                            index
                                                        ].description =
                                                            e.target.value;
                                                        setActiveItems(
                                                            (prevItems) =>
                                                                prevItems.map(
                                                                    (i) =>
                                                                        i._id ===
                                                                        item._id
                                                                            ? {
                                                                                  ...i,
                                                                                  prices: updatedPrices,
                                                                              }
                                                                            : i
                                                                )
                                                        );
                                                    }}
                                                    className="border rounded-md p-2 flex-grow"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Amount"
                                                    value={price.amount}
                                                    onChange={(e) => {
                                                        const updatedPrices = [
                                                            ...item.prices,
                                                        ];
                                                        updatedPrices[
                                                            index
                                                        ].amount = parseFloat(
                                                            e.target.value
                                                        );
                                                        updateItem({
                                                            ...item,
                                                            prices: updatedPrices,
                                                        });
                                                    }}
                                                    className="border rounded-md p-2 w-24"
                                                />
                                                <button
                                                    className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                                    onClick={() =>
                                                        updateItem({
                                                            ...item,
                                                            prices: item.prices.filter(
                                                                (_, i) =>
                                                                    i !== index
                                                            ),
                                                        })
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                                        onClick={() => {
                                            updateItem({
                                                ...item,
                                                prices: [
                                                    ...item.prices,
                                                    {
                                                        description: "",
                                                        amount: 0,
                                                    },
                                                ],
                                            });
                                        }}
                                    >
                                        Add Price
                                    </button>
                                </div>

                                <div>
                                    {/* Editable Sort Order */}
                                    <input
                                        type="number"
                                        value={item.sortOrder}
                                        onChange={(e) =>
                                            updateItem({
                                                ...item,
                                                sortOrder: parseInt(
                                                    e.target.value
                                                ),
                                            })
                                        }
                                        className="border rounded-md p-2 w-24"
                                    />
                                </div>
                                <div>
                                    {/* Editable Side Dish */}
                                    <input
                                        type="checkbox"
                                        id="sideDish"
                                        name="sideDish"
                                        value="sideDish"
                                        checked={item.sideDish}
                                        onChange={(e) =>
                                            updateItem({
                                                ...item,
                                                sideDish: e.target.checked,
                                            })
                                        }
                                    />
                                </div>

                                <div className="flex gap-2 mt-4">
                                    <button
                                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700"
                                        onClick={() => updateItemAPI(item)}
                                    >
                                        Save
                                    </button>
                                    <button
                                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700"
                                        onClick={() => setEditableItemId(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700"
                                        onClick={() => setItemToDelete(item)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div
                                onClick={() => setEditableItemId(item._id)}
                                className=""
                            >
                                <div className="flex flex-row justify-between">
                                    <div className="mr-4">
                                        <div className="">
                                            <strong>{item.title}</strong>
                                        </div>
                                        <div className="mb-2">
                                            <i>{item.description}</i>{" "}
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <ul className="pl-4 list-none">
                                            {item.prices.map((price, index) => (
                                                <li key={index}>
                                                    {price.description}
                                                    {price.amount.toFixed(2)} €
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                {/* Display Title */}

                                {/* Display Description */}

                                {/* Display Prices */}

                                <div className="flex gap-2"></div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Items;
